import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Building2, Zap, Leaf } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { setPageMeta } from "@/components/seo";
import { IMAGES, SERVICE_SECTIONS } from "@/data/site";

const ICONS = { residential: HomeIcon, commercial: Building2, level2: Zap };

const ServiceSection = ({ section, index }) => {
  const dark = section.id === "level2";
  const grey = section.id === "commercial";
  const flip = index % 2 === 1;
  const Icon = ICONS[section.id];

  return (
    <section
      id={section.id}
      className={`scroll-mt-28 ${dark ? "bg-ink dot-texture" : grey ? "bg-[#F4F6F5]" : "bg-white"}`}
      data-testid={`service-section-${section.id}`}
    >
      <div className="container-site py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal className={flip ? "lg:order-2" : ""}>
            <img
              src={section.image}
              alt={section.imageAlt}
              className={`w-full aspect-[4/3] object-cover ${dark ? "border border-steel" : ""}`}
              loading="lazy"
            />
          </Reveal>
          <Reveal delay={0.1} className={flip ? "lg:order-1" : ""}>
            <span className={`inline-flex w-12 h-12 items-center justify-center ${dark ? "bg-volt" : "bg-ink"}`}>
              <Icon className="w-5 h-5 text-white" />
            </span>
            <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mt-5 ${dark ? "text-white" : "text-ink"}`}>
              {section.title}
            </h2>
            <p className={`text-base mt-4 leading-relaxed ${dark ? "text-gray-400" : "text-gray-600"}`}>{section.intro}</p>
            <ul className="mt-6 space-y-2.5">
              {section.points.map((point, i) => {
                const name = typeof point === "string" ? point : point.name;
                const desc = typeof point === "string" ? null : point.desc;
                return (
                  <li key={i} className="flex items-start gap-3" data-testid={`service-point-${section.id}-${i}`}>
                    <span className={`mt-2 w-2 h-2 shrink-0 ${dark ? "bg-volt-bright" : "bg-volt"}`} aria-hidden="true" />
                    <span>
                      <span className={`text-sm sm:text-base font-semibold ${dark ? "text-white" : "text-ink"}`}>{name}</span>
                      {desc && (
                        <span className={`block text-sm mt-1 leading-relaxed font-normal ${dark ? "text-gray-400" : "text-gray-600"}`}>{desc}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { hash } = useLocation();

  useEffect(() => {
    setPageMeta(
      "Residential, Commercial & Level 2 Electrical Services | LAD CO Electrical — Northern Rivers NSW",
      "Residential, commercial and Level 2 electrical services across the Northern Rivers NSW — new builds, switchboard upgrades, 24/7 emergency service, metering, EV charging, energy efficiency and more."
    );
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [hash]);

  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title="Electrical Services"
        sub="Residential, commercial and Level 2 electrical services across the Northern Rivers, NSW."
      />

      {SERVICE_SECTIONS.map((section, i) => (
        <ServiceSection key={section.id} section={section} index={i} />
      ))}

      <section id="energy-efficiency" className="scroll-mt-28 bg-white" data-testid="service-section-energy-efficiency">
        <div className="container-site py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <Reveal className="lg:order-2">
              <img
                src={IMAGES.battery}
                alt="LAD CO Electrical installing a home battery energy storage system"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </Reveal>
            <Reveal delay={0.1} className="lg:order-1">
              <span className="inline-flex w-12 h-12 items-center justify-center bg-ink">
                <Leaf className="w-5 h-5 text-white" />
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mt-5 text-ink">
                Energy Efficiency
              </h2>
              <p className="text-base mt-4 leading-relaxed text-gray-600">
                Energy efficiency is about using less electricity to achieve the same result — lower power bills, less strain on your electrical system and a smaller environmental footprint, without giving up comfort or capability. For most homes and businesses, the biggest savings come from upgrading the way lighting, power and equipment are controlled and consumed.
              </p>
              <p className="text-base mt-4 leading-relaxed text-gray-600">
                LAD CO Electrical helps Northern Rivers customers cut their energy use with practical, proven upgrades: replacing old lighting with efficient LED fittings, installing smart home automation and controls so power is only used when it's needed, and upgrading ageing electrical systems to modern, efficient alternatives. For new builds and larger developments, we can also assist with comprehensive energy planning so efficiency is built in from the start.
              </p>
              <p className="text-base mt-4 leading-relaxed text-gray-600">
                If you're interested in solar or battery storage, we can put you in touch with a trusted specialist and take care of the associated electrical work.
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  "LED Lighting Upgrades",
                  "Smart Home Automation & Controls",
                  "Energy Monitoring & Optimisation",
                  "Efficient Power Systems & Upgrades",
                  "Energy Planning for New Builds & Developments",
                  "Solar & Battery Solutions — via a trusted specialist",
                ].map((point, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`service-point-energy-${i}`}>
                    <span className="mt-2 w-2 h-2 shrink-0 bg-volt" aria-hidden="true" />
                    <span className="text-sm sm:text-base font-semibold text-ink">{point}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-gray-200" data-testid="services-cta">
        <div className="container-site py-14 lg:py-20 flex flex-col items-center text-center">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Ready to Get Started?</h2>
            <p className="text-gray-600 text-base mt-3 max-w-xl">
              Talk to LAD CO Electrical about your residential, commercial or Level 2 electrical requirements.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <Link to="/quote" className="btn-volt" data-testid="services-quote-cta">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-ghost-dark" data-testid="services-contact-cta">
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Services;
